export const meta = {
    title: "Project Analysis | CTrees",
    desc: "Trusted carbon and activity data for carbon markets",
    img: "",
};

export const hero = {
    title: "Project Analysis",
    subheader:
        "Trusted scientific data to plan, implement, and monitor nature-based projects.",
    link: "/contact",
    buttonText: "REQUEST A DEMO",
    bgImg:
        "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/hero/project-analysis.jpg",
    bgImgStyle: "h-screen bg-black",
    bgImgPosition: {
        mobile: "object-center",
        tablet: "object-center",
        desktop: "object-center",
    },
};

export const difference = {
    heading: "Measure your impact",
    description:
        "Carbon markets are changing. Get ahead with scientific data that meets and exceeds methodology requirements. Our flexible data solutions prepare projects for issuance and verify outcomes.",
    cards: [
        {
            id: 1,
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/landing/25_01+Data+Card_REDD%2BAI.png",
            imageAlt: "Comprehensive data",
            title: "Comprehensive data",
            description:
                "Access groundbreaking data on carbon and land activity, all backed by peer-reviewed science. Since 2022, CTrees has produced the first global land carbon map, the first global radar alert system, and the first pantropic degradation maps.",
            hoverDescription:
                "",
        },
        {
            id: 2,
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/news/25_09+NYCW_Sassan_speaking.jpeg",
            imageAlt: "Activity & Carbon Data",
            title: "Science expertise",
            description:
                "Engage with experts in remote sensing and forest ecology. CTrees mapped 43 jurisdictions for Verra's VM0048 methodology and built performance monitoring for 8 jurisdictions under ART/TREES. Our scientists regularly publish in leading academic journals.",
            hoverDescription:
                "",
        },
        {
            id: 3,
            image:
                "https://ctrees-website.s3.amazonaws.com/webv3/products/beats/activity-data-beat-4.png",
            title: "Custom analysis",
            description:
                "Generate results based on your needs, for any date range, ecosystem, or stage in the project lifecycle. CTrees works with developers, investors, and standards to deliver scientific insights that matter. CTrees works with developers, investors, and standards to deliver scientific insights that matter.",
            hoverDescription:
                "",
        },
    ],
};

export const participants = {
    heading: "Data for Action",
    ctaText: "Get in touch",
    ctaLink: "/contact",
    items: [
        {
            title: "For Project Developers",
            body: "Evaluate feasibility for new projects, set baselines for registration, and monitor ongoing performance and risk.",
        },
        {
            title: "For Investors",
            body: "Find and evaluate projects to invest in with confidence. Monitor and de-risk project performance.",
        },
    ],
};

export const methodologies = {
    heading: "Scientific Insights",
    description:
        "Work with CTrees scientists to develop actionable maps and metrics for project design and verification. From off-the-shelf data to customized solutions, CTrees makes your project or portfolio’s carbon impact visible.",
    ctaText: "GET IN TOUCH",
    ctaLink: "/contact",
    tabs: [
        {
            id: "redd",
            label: "REDD+",
            image:
                "https://images.unsplash.com/photo-1510220823173-016d47b80a53?q=80&w=1480&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
            body: {
                title: "Evaluating JREDD+ programs",
                text: "For a large investor in jurisdictional REDD+ credits, CTrees evaluated ART/TREES documentation for four jurisdictions and designed a performance monitoring system for ongoing MRV. CTrees scientists engaged with agency staff to compare independent data to reported results, and presented results to the jurisdiction. The jurisdiction is now moving towards issuance.",
            },
        },
        {
            id: "arr",
            label: "ARR",
            image:
                "https://images.unsplash.com/photo-1635326530588-5fda7dce759e?q=80&w=1474&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
            body: {
                title: "Tracking regrowth in Kenya",
                text: "For a project developer, CTrees evaluated a restoration site in Kenya, establishing a stocking index using radar data for issuance under VM0047. By applying a bioaccumulation growth curve tailored to the local ecosystem, CTrees project credit issuance for the next decade. The developer is now moving forward towards issuance.",
            },
        },
        {
            id: "ifm",
            label: "IFM",
            image:
                "https://www.istockphoto.com/photo/aerial-drone-view-of-the-jungle-in-the-yucat%C3%A1n-plateau-mexico-gm2223733781-639915297",
            body: {
                title: "Evaluating performance in Mexico",
                text: "For a large buyer, CTrees evaluated four IFM projects in Mexico issuing credits under the Climate Action Reserve’s Mexico Forest Protocol. CTrees scientists reviewed historic harvest rates and carbon stocks to assess additionality. CTrees’ scientific analysis and presentation resulted in a revision of the project’s carbon crediting to better match the data. ",
            },
        },
        {
            id: "blue",
            label: "Blue carbon",
            image:
                "https://www.istockphoto.com/photo/tall-mangrove-trees-rhizophora-gm949302290-259143144",
            body: {
                title: "Taking stock in Myanmar",
                text: "For a large buyer considering portfolio investment in blue carbon, CTrees provided independent evaluation of three mangrove restoration projects in Myanmar and India. With data tailored to local conditions, CTrees scientists evaluated biomass carbon and activity in the project site, surrounding areas, and jurisdiction. CTrees compared its results to data reported under VM0048. The information informed the buyers’ credit purchasing decisions.",
            },
        },
    ],
};

export const lifeCycle = {
    title: "Project Life Cycle",
    subheader:
        "High-quality analytics to take your carbon investment through the life cycle.",
    items: [
        {
            id: "feasibility",
            title: "1. Feasibility & due diligence",
            body: "Evaluate project potential with desktop assessments, historical baselines, and risk screening to determine viability before investment.",
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/beats/feasibility.png",
            imageAlt: "Feasibility and due diligence analytics",
        },
        {
            id: "baselines",
            title: "2. Baselines setting",
            body: "Establish accurate baselines with scenario modeling, leakage analysis, and MRV planning to ensure compliance and optimize project design.",
            image:
                "https://images.unsplash.com/photo-1596328546171-77e37b5e8b3d?q=80&w=1480&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
            imageAlt: "Baselines setting analytics",
        },
        {
            id: "monitoring",
            title: "3. Monitoring & verification",
            body: "Track project performance with real-time alerts, disturbance monitoring, and annual reporting to support verification and credit issuance.",
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/beats/monitoring.png",
            imageAlt: "Monitoring and verification dashboard",
        },
    ],
};

export const dataDeliverables = {
    heading: "Available Data",
    tableData: [
        {
            id: 1,
            dataset: "Forest cover change",
            resolution: "30m",
            geographicExtent: "Wet tropics",
            temporalCoverage: "1990-2025, annual",
            metrics: "Deforestation, degradation, primary & secondary forests",
        },
        {
            id: 2,
            dataset: "Forest productivity",
            resolution: "100m",
            geographicExtent: "Global",
            temporalCoverage: "Average over past 40 years",
            metrics: "Carbon growth curves",
        },
        {
            id: 3,
            dataset: "Forest change alerts",
            resolution: "10m",
            geographicExtent: "Global",
            temporalCoverage: "2018-present, every 2 weeks",
            metrics: "Reversal risk",
        },
        {
            id: 4,
            dataset: "Biomass carbon",
            resolution: "100m and 30m",
            geographicExtent: "Global",
            temporalCoverage: "2000-2025, annual",
            metrics: "Carbon stocks, emissions, removals",
        },
        {
            id: 5,
            dataset: "Soil carbon",
            resolution: "100m",
            geographicExtent: "Global",
            temporalCoverage: "2020",
            metrics: "Carbon stock",
        },
    ],
};